<template>
  <div class="container">
    <div class="box-title" style="display:flex;justify-content: space-between">
      <h4>
        <i class="fal fa-box-check"></i>
        ລາຍລະອຽດການຢືນຢັນຮັບພັດສະດຸ
        <span class="countTotal" v-if="proof">{{ proof.bill_number }}</span>
      </h4>
      <button class="button" @click="$router.push({ name: 'delivery.proof.queue' })">ກັບຄືນ</button>
    </div>

    <p v-if="lastReportError" class="report-error">{{ lastReportError }}</p>
    <p v-if="loading" class="empty-line">ກຳລັງໂຫລດ...</p>

    <div v-else-if="proof" class="columns">
      <div class="column is-6">
        <div class="box">
          <label class="label">ຮູບທີ່ລູກຄ້າຖ່າຍ</label>

          <!-- Purged by retention: the proof and its figures survive, so this
               says the photograph is gone rather than showing a broken frame. -->
          <p v-if="!proof.photo_available" class="empty-line">
            ຮູບຖືກລຶບຕາມກຳນົດເກັບຮັກສາແລ້ວ
          </p>
          <img v-else-if="photoUrl" :src="photoUrl" class="proof-photo" @error="onPhotoError">
          <p v-else class="empty-line">ໂຫລດຮູບບໍ່ສຳເລັດ</p>
        </div>
      </div>

      <div class="column is-6">
        <div class="box">
          <!-- Stated before any decision. Head office does not sign bills, so
               this is what tells them whether the branch still owes the keying. -->
          <p class="bill-state" :class="billStateClass">{{ billStateLabel }}</p>

          <table class="table is-fullwidth">
            <tbody>
              <tr><th>ເລກບິນ</th><td>{{ proof.bill_number || proof.shipment_id }}</td></tr>
              <tr><th>ສາຂາ</th><td>{{ proof.branch_name || ('#' + proof.end_branch_id) }}</td></tr>
              <tr><th>ລູກຄ້າແຈ້ງເມື່ອ</th><td>{{ shortDateTime(proof.submitted_at) }}</td></tr>
              <tr><th>ຜົນການກວດ</th><td>{{ reviewLabel(proof.status) }}</td></tr>
              <tr v-if="proof.reviewed_at"><th>ກວດເມື່ອ</th><td>{{ shortDateTime(proof.reviewed_at) }}</td></tr>
              <!-- A measurement, not a judgement. -->
              <tr>
                <th>ຕຳແໜ່ງຕອນຢືນຢັນ</th>
                <td>
                  {{ proximityLabel }}
                  <span v-if="distanceText" class="muted">{{ distanceText }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- The REASON, not just the note. A note is typed for one code out
               of six, so keying off it left most rejections showing nothing —
               and a reviewer reopening the row could not see what was decided. -->
          <p v-if="proof.status === 'rejected'" class="reject-note">
            {{ rejectionText }}
          </p>

          <div class="actions">
            <!-- Stays available on a rejected proof: correcting one's own
                 earlier rejection is a repair (backend D16). -->
            <button v-if="canApprove" class="button is-success" :disabled="submitting"
                    @click="approve">ຢືນຢັນ</button>
            <!-- Absent, not disabled, once decided either way. -->
            <button v-if="canReject" class="button is-danger" :disabled="submitting"
                    @click="rejectOpen = true">ປະຕິເສດ</button>

            <!-- The one action on this screen that moves money.
                 Shown ONLY on the server's answer: `may_sign` is the AND of "this
                 bill is signable" and "this caller holds the permission". There
                 is deliberately no $can() here — this app reads roles out of
                 local storage and the authority is a per-user permission, so a
                 role check would show the button to the wrong people. -->
            <button v-if="canSignOnBehalf" class="button is-warning" :disabled="submitting"
                    @click="signOpen = true">ເຊັນຮັບແທນສາຂາ</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reject dialog -->
    <div class="modal" :class="{ 'is-active': rejectOpen }">
      <div class="modal-background" @click="rejectOpen = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">ປະຕິເສດຫຼັກຖານ</p>
        </header>
        <section class="modal-card-body">
          <!-- Said before the reviewer chooses, not after they submit. -->
          <p class="notice">
            ການປະຕິເສດເປັນການບັນທຶກຄວາມເຫັນ ບໍ່ແມ່ນການລຶບ —
            ລາຍການນີ້ຍັງຄົງຢູ່ໃນລາຍງານ ແລະ ລູກຄ້າຈະໄດ້ຮັບແຈ້ງເພື່ອຖ່າຍໃໝ່
          </p>

          <label class="label">ເຫດຜົນ</label>
          <select class="input" v-model="rejectCode">
            <option value="" disabled>ເລືອກເຫດຜົນ</option>
            <option v-for="c in codes" :key="c.code" :value="c.code">{{ codeLabel(c.code) }}</option>
          </select>

          <div v-if="noteRequired" style="margin-top:12px">
            <label class="label">ລາຍລະອຽດ</label>
            <textarea class="input" rows="3" maxlength="255" v-model="rejectNote"></textarea>
          </div>

          <p v-if="rejectError" class="reject-note">{{ rejectError }}</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-danger" :disabled="submitting" @click="reject">ບັນທຶກ</button>
          <button class="button" @click="rejectOpen = false">ຍົກເລີກ</button>
        </footer>
      </div>
    </div>

    <!-- Sign on behalf of the branch -->
    <div class="modal" :class="{ 'is-active': signOpen }">
      <div class="modal-background" @click="signOpen = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">ເຊັນຮັບແທນສາຂາ</p>
        </header>
        <section class="modal-card-body">
          <!-- Three things, in the order they matter. The middle one is the
               property most likely to be doubted — and doubting it is
               reasonable, because the older bulk path in this system genuinely
               does credit the wrong branch. -->
          <p class="notice">
            ການກົດນີ້ <strong>ປິດບິນຈິງ</strong> —
            ຄ່າຂົນສົ່ງ, COD, ກະເປົາເງິນ ແລະ ພ້ອຍ ຈະລົງ
            <strong>ສາຂາປາຍທາງ ບໍ່ແມ່ນສຳນັກງານໃຫຍ່</strong>
            ຄືກັບວ່າສາຂາເຊັນເອງ. ແລະ <strong>ຍົກເລີກຈາກໜ້ານີ້ບໍ່ໄດ້</strong>
          </p>

          <label class="label">ຮູບລາຍເຊັນ</label>
          <!-- The ACTING USER's own image. The customer's photograph above is
               the evidence that permits this sign — the backend refuses to reuse
               it as the bill's signature, because a bill's signature outlives
               the proof's 180-day retention. So no shortcut is offered here. -->
          <input type="file" accept="image/*" @change="onSignatureChosen">
          <p class="muted">ຮູບບິນພ້ອມລາຍເຊັນ ຫຼື ຫຼັກຖານການອະນຸມັດ (ສູງສຸດ 5MB)</p>

          <div style="margin-top:12px">
            <label class="label">ໝາຍເຫດ (ບໍ່ບັງຄັບ)</label>
            <input class="input" type="text" maxlength="255" v-model="signNote">
          </div>

          <p v-if="signError" class="reject-note">{{ signError }}</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-warning" :disabled="submitting" @click="signOnBehalf">
            ຢືນຢັນເຊັນຮັບແທນ
          </button>
          <button class="button" @click="signOpen = false">ຍົກເລີກ</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import DeliveryProofApi from '@/Mixins/DeliveryProof/DeliveryProofApi';

/**
 * One proof, and head office's decision on it.
 *
 * Head office does NOT sign bills — approving records that the evidence is
 * accepted and nothing else (design D1). What closes a bill is the branch keying
 * it on its own screen, which is why the bill's current state is stated here
 * rather than left for the reviewer to guess.
 */
export default {
    name: 'DeliveryProofDetail',
    mixins: [DeliveryProofApi],

    data() {
        return {
            proof: null,
            photoUrl: null,
            photoRetried: false,
            codes: [],
            rejectOpen: false,
            rejectCode: '',
            rejectNote: '',
            rejectError: null,
            loading: false,
            submitting: false,

            // The server's answer about signing on behalf. Null until asked, and
            // null is "do not offer it" — a 404 (backend not deployed) leaves it
            // that way, which is what makes this screen safe to ship first.
            signEligibility: null,
            signOpen: false,
            signatureFile: null,
            signNote: '',
            signError: null,
        };
    },

    computed: {
        canApprove() {
            return this.proof && this.proof.status !== 'approved';
        },
        /**
         * Only something undecided can be rejected.
         *
         * An acceptance cannot be withdrawn (backend D16), and rejecting an
         * already-rejected proof decides nothing while notifying the customer a
         * second time — they have been told once and the retake is already open
         * to them.
         */
        canReject() {
            return this.proof && this.proof.status === 'pending';
        },
        noteRequired() {
            const found = this.codes.find((c) => c.code === this.rejectCode);
            return !!(found && found.requires_note);
        },

        /**
         * The server's answer, and only the server's answer.
         *
         * `may_sign` already carries both halves — the bill's eligibility and
         * this caller's permission — so this screen neither re-derives the first
         * (it would drift from the backend's rules) nor guesses the second (it
         * cannot: `roleUser` holds no permissions).
         */
        canSignOnBehalf() {
            return !!(this.signEligibility && this.signEligibility.may_sign);
        },
        billStateLabel() {
            return {
                open: 'ບິນນີ້ຍັງບໍ່ໄດ້ເຊັນຮັບໃນລະບົບ — ສາຂາຍັງຕ້ອງໄປເຊັນຮັບ',
                keyed_by_person: 'ບິນນີ້ເຊັນຮັບແລ້ວ',
                auto_signed: 'ບິນນີ້ຖືກລະບົບປິດໃຫ້ອັດຕະໂນມັດ ບໍ່ແມ່ນພະນັກງານເຊັນ',
            }[this.proof && this.proof.bill_closure_state] || '—';
        },
        billStateClass() {
            const state = this.proof && this.proof.bill_closure_state;
            if (state === 'keyed_by_person') return 'state-ok';
            if (state === 'auto_signed') return 'state-info';
            return 'state-warn';
        },
        proximityLabel() {
            const state = this.proof && this.proof.proximity && this.proof.proximity.state;
            return {
                at_branch: 'ຢູ່ບໍລິເວນສາຂາ',
                nearby: 'ໃກ້ສາຂາ',
                away: 'ຫ່າງຈາກສາຂາ',
                imprecise: 'ສັນຍານບໍ່ແມ່ນຢຳ',
                unknown: 'ບໍ່ມີຂໍ້ມູນຕຳແໜ່ງ',
            }[state] || 'ບໍ່ມີຂໍ້ມູນຕຳແໜ່ງ';
        },
        /** The published reason in words, with the note appended when there is one. */
        rejectionText() {
            const label = this.codeLabel(this.proof && this.proof.rejection_code);
            const note = this.proof && this.proof.rejection_note;
            return note ? `${label} — ${note}` : label;
        },

        distanceText() {
            const p = this.proof && this.proof.proximity;
            if (!p || p.distance_m === null || p.distance_m === undefined) return '';
            const d = p.distance_m < 1000 ? `${p.distance_m} ແມັດ` : `${(p.distance_m / 1000).toFixed(1)} ກມ.`;
            return p.accuracy_m ? `${d} (±${p.accuracy_m} ມ.)` : d;
        },
    },

    mounted() {
        this.fetchDetail();
        this.deliveryProofConfig()
            .then(({ data }) => { this.codes = data.data.rejection_codes || []; })
            .catch(() => { this.codes = []; });
    },

    methods: {
        fetchDetail() {
            this.loading = true;
            this.lastReportError = null;

            this.deliveryProofDetail(this.$route.params.id)
                .then(({ data }) => {
                    this.proof = data.data;
                    this.photoUrl = this.proof.photo_url;
                    this.photoRetried = false;
                    this.fetchSignEligibility();
                })
                .catch((error) => {
                    this.proof = null;
                    this.handleReportError(error);
                })
                .finally(() => { this.loading = false; });
        },

        /**
         * Ask whether this bill may be closed on the branch's behalf, by this
         * caller.
         *
         * Any failure leaves the action absent rather than surfacing an error:
         * on a backend without this endpoint the call 404s, and "no button" is
         * the correct reading of that.
         */
        fetchSignEligibility() {
            const bill = this.proof && this.proof.bill_number;

            if (!bill) {
                this.signEligibility = null;
                return;
            }

            this.deliveryProofSignOnBehalfEligibility(bill)
                .then(({ data }) => { this.signEligibility = data.data; })
                .catch(() => { this.signEligibility = null; });
        },

        onSignatureChosen(event) {
            this.signatureFile = (event.target.files && event.target.files[0]) || null;
            this.signError = null;
        },

        /**
         * Close the bill in the branch's name.
         *
         * On success the proof is REFETCHED rather than patched: the bill-state
         * line is the sentence the reviewer acted on ("ສາຂາຍັງຕ້ອງໄປເຊັນຮັບ"),
         * and it must stop saying that because the server says so — not because
         * this screen assumed a transition it never observed.
         */
        signOnBehalf() {
            this.signError = null;

            // Blocked here as well as server-side, so the refusal is not a round trip.
            if (!this.signatureFile) {
                this.signError = 'ກະລຸນາເລືອກຮູບລາຍເຊັນ';
                return;
            }

            const form = new FormData();
            form.append('bill_number', this.proof.bill_number);
            form.append('signature', this.signatureFile);
            if (this.signNote.trim()) form.append('note', this.signNote.trim());

            this.submitting = true;

            this.deliveryProofSignOnBehalf(form)
                .then(() => {
                    this.signOpen = false;
                    this.signatureFile = null;
                    this.signNote = '';
                    this.fetchDetail();
                })
                .catch((error) => {
                    // The server's own words. `already_signed` (409) is a normal
                    // answer — somebody closed the bill first — and a local
                    // mapping would go stale the day an outcome is added.
                    this.signError = error?.response?.data?.message || 'ເຊັນຮັບແທນບໍ່ສຳເລັດ';

                    // If it was already closed, the screen is now out of date.
                    if (error?.response?.status === 409) {
                        this.fetchDetail();
                    }
                })
                .finally(() => { this.submitting = false; });
        },

        /**
         * The photo address is signed and expires. Refetching the proof is what
         * issues a fresh one, so a page left open recovers ONCE before the
         * reviewer is told anything failed.
         */
        onPhotoError() {
            if (this.photoRetried) {
                this.photoUrl = null;
                return;
            }
            this.photoRetried = true;
            this.deliveryProofDetail(this.$route.params.id)
                .then(({ data }) => { this.photoUrl = data.data.photo_url; })
                .catch(() => { this.photoUrl = null; });
        },

        approve() {
            this.submitting = true;
            this.deliveryProofApprove(this.proof.id)
                .then(({ data }) => { this.proof = data.data; })
                .catch((error) => this.handleReportError(error))
                .finally(() => { this.submitting = false; });
        },

        reject() {
            this.rejectError = null;

            if (!this.rejectCode) {
                this.rejectError = 'ກະລຸນາເລືອກເຫດຜົນ';
                return;
            }
            // Blocked here as well as server-side, so the refusal is not a round trip.
            if (this.noteRequired && !this.rejectNote.trim()) {
                this.rejectError = 'ເຫດຜົນນີ້ຕ້ອງໃສ່ລາຍລະອຽດ';
                return;
            }

            this.submitting = true;
            const body = { rejection_code: this.rejectCode };
            if (this.rejectNote.trim()) body.rejection_note = this.rejectNote.trim();

            this.deliveryProofReject(this.proof.id, body)
                .then(({ data }) => {
                    this.proof = data.data;
                    this.rejectOpen = false;
                    this.rejectCode = '';
                    this.rejectNote = '';
                })
                .catch((error) => {
                    // 409 = already approved; the server's message explains why.
                    this.rejectError = error?.response?.data?.message || 'ບັນທຶກບໍ່ສຳເລັດ';
                })
                .finally(() => { this.submitting = false; });
        },

        codeLabel(code) {
            return {
                parcel_still_at_branch: 'ພັດສະດຸຍັງຢູ່ສາຂາ ຍັງບໍ່ໄດ້ສົ່ງມອບ',
                not_the_receiver: 'ຜູ້ມາຮັບບໍ່ແມ່ນຜູ້ຮັບໃນບິນ',
                wrong_bill: 'ຮູບບໍ່ກົງກັບບິນນີ້',
                photo_unusable: 'ຮູບບໍ່ຊັດ ຫຼື ບໍ່ແມ່ນພັດສະດຸ',
                already_signed: 'ບິນນີ້ເຊັນຮັບແລ້ວກ່ອນໜ້າ',
                other: 'ອື່ນໆ',
                // An unknown code — one added to the API after this build —
                // reads as unstated rather than printing a raw identifier.
            }[code] || 'ບໍ່ໄດ້ລະບຸເຫດຜົນ';
        },

        reviewLabel(status) {
            return {
                pending: 'ຍັງບໍ່ໄດ້ກວດ',
                approved: 'ຢືນຢັນແລ້ວ',
                rejected: 'ປະຕິເສດແລ້ວ',
            }[status] || status;
        },

        shortDateTime(value) {
            if (!value) return '—';
            const d = new Date(value);
            return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.proof-photo {
    width: 100%;
    max-height: 420px;
    object-fit: contain;
}

.bill-state {
    padding: 10px;
    border-radius: 4px;
    margin-bottom: 12px;
    font-weight: 600;
}

.state-warn {
    background: #fff8e1;
    color: #8a6100;
}

.state-ok {
    background: #e8f5e9;
    color: #1b5e20;
}

.state-info {
    background: #eceff1;
    color: #37474f;
}

.notice {
    padding: 10px;
    margin-bottom: 12px;
    background: #fff8e1;
    color: #8a6100;
    border-radius: 4px;
}

.reject-note {
    margin-top: 10px;
    color: #b71c1c;
}

.report-error {
    margin-bottom: 12px;
    padding: 10px;
    background: #fdecea;
    color: #b71c1c;
    border-radius: 4px;
}

.actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}

.muted {
    color: #777;
    margin-left: 6px;
}

.empty-line {
    padding: 24px 0;
    text-align: center;
    color: #666;
}
</style>
